import type { WorkContextTarget, SwitchContextResult, ContextsList } from '@turn-connect/shared';
/**
 * Accounts + session context + activities admin API module.
 *
 * Extracted from the AdminService god class in services/auth.ts and converted
 * to the central web API client. Method names, signatures, and observable
 * behavior (dedup keys, error messages, fallback shapes, console logging,
 * return-[]-on-error semantics) mirror the originals so the composed
 * `adminService` export stays drop-in compatible.
 */
import { webApiClient } from '../apiClient';
import { dedup, extractItems } from './helpers';
import type { Location } from '../auth';
import type { Activity } from '@turn-connect/shared';
import { refreshAccountNames } from '../../queries/nameCaches';

// Mirrors auth.ts extractServerError, operating on the API client's parsed
// errorBody instead of raw response text. Backend errors come back as JSON
// `{"error":"..."}` for the Fiber handlers; some surfaces also use
// `{"message":"..."}`. Returns null when neither is present so callers can
// fall back to a generic hand-written message.
function serverErrorFromBody(errorBody: any): string | null {
  if (errorBody && typeof errorBody === 'object') {
    if (typeof errorBody.error === 'string' && errorBody.error.trim()) return errorBody.error;
    if (typeof errorBody.message === 'string' && errorBody.message.trim()) return errorBody.message;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Account logo
// ---------------------------------------------------------------------------

// Account Logo/Image helper (OpenAPI exposes account-level images)
export function getAccountLogoUrl(accountId: string): string {
  // Prefer a consistent path similar to location logos
  return `${webApiClient.getBaseUrl()}/accounts/${accountId}/logo`;
}

export async function uploadAccountLogo(accountId: string, file: Blob): Promise<void> {
  const form = new FormData();
  form.append('logo', file);
  const res = await webApiClient.uploadMultipart<void>(`/accounts/${accountId}/logo`, form);
  if (!res.success) {
    throw new Error(res.error || 'Failed to upload account logo');
  }
}

// ---------------------------------------------------------------------------
// Accounts CRUD + dashboard
// ---------------------------------------------------------------------------

export async function getAllAccounts(includeDeleted = false): Promise<any[]> {
  return dedup(`getAllAccounts:${includeDeleted}`, async () => {
    // GET /accounts pages (default 20, max 100) for a system admin — read
    // every page so no account past the first page silently disappears.
    const PAGE = 100;
    const all: any[] = [];
    for (let offset = 0; ; offset += PAGE) {
      const url = `/accounts?include_deleted=${includeDeleted ? 'true' : 'false'}&limit=${PAGE}&offset=${offset}`;
      const res = await webApiClient.get<any>(url);
      if (!res.success) {
        if (res.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        } else if (res.status === 403) {
          // Prefer the backend's actual error message — it knows which
          // specific permission is missing. Fall back to a generic
          // "no permission" rather than the old "Admin permissions
          // required" wording, which lied when this endpoint surfaced
          // on non-admin screens (e.g. the location detail modal).
          throw new Error(serverErrorFromBody(res.errorBody) ?? "You don't have permission to view accounts.");
        }
        throw new Error(`Failed to fetch accounts: ${res.status} - ${res.error}`);
      }
      const items = extractItems(res.data, ['accounts']);
      all.push(...items);
      const more = (res.data as any)?.has_more === true;
      if (!more || items.length === 0) break;
    }
    return all;
  });
}

export interface AccountsPageParams {
  /** Matches the account's name or email. */
  search?: string;
  includeDeleted?: boolean;
  /** 'name' lists A→Z; the server default is newest first. */
  sortBy?: 'name';
  limit: number;
  offset: number;
}

/**
 * One page of the platform's accounts, filtered, sorted and counted on the
 * server — the Platform Admin list, which used to load every account and
 * filter them in the browser.
 */
export async function listAccountsPage(p: AccountsPageParams): Promise<{ items: any[]; total: number }> {
  const params = new URLSearchParams();
  if (p.search?.trim()) params.set('search', p.search.trim());
  params.set('include_deleted', p.includeDeleted ? 'true' : 'false');
  if (p.sortBy) params.set('sort_by', p.sortBy);
  params.set('limit', String(p.limit));
  params.set('offset', String(p.offset));
  const res = await webApiClient.get<any>(`/accounts?${params.toString()}`);
  if (!res.success) {
    if (res.status === 401) throw new Error('Authentication required. Please log in again.');
    if (res.status === 403) throw new Error(serverErrorFromBody(res.errorBody) ?? "You don't have permission to view accounts.");
    throw new Error(`Failed to fetch accounts: ${res.status} - ${res.error}`);
  }
  const items = extractItems(res.data, ['accounts']);
  const total = typeof (res.data as any)?.total === 'number' ? (res.data as any).total : items.length;
  return { items, total };
}

/**
 * Account ids and names, by name — the accounts dashboard's scope (every live
 * account for a system admin, the token's account otherwise) without its
 * metrics. For pickers and anything else that only needs names.
 */
export async function getAccountSummaries(): Promise<Array<{ id: string; name: string }>> {
  return dedup('getAccountSummaries', async () => {
    const res = await webApiClient.get<any>('/accounts/summaries');
    if (!res.success) {
      if (res.status === 401) throw new Error('Authentication required. Please log in again.');
      if (res.status === 403) throw new Error(serverErrorFromBody(res.errorBody) ?? "You don't have permission to view accounts.");
      throw new Error(`Failed to fetch accounts: ${res.status} - ${res.error}`);
    }
    const accounts = (res.data as { accounts?: Array<{ id: string; name: string }> })?.accounts;
    return Array.isArray(accounts) ? accounts.map((a) => ({ id: a.id, name: a.name })) : [];
  });
}

export async function getAccountsDashboard(opts?: {
  include_locations?: boolean;
  account_id?: string;
  // Deleted (disabled) accounts are excluded by the server unless asked for.
  // Set include_deleted for the "Show Disabled" toggle; exclude_deleted is
  // retained because it still overrides include_deleted server-side.
  include_deleted?: boolean;
  exclude_deleted?: boolean;
  // Account-level sort.
  sort_by?: string;
  sort_desc?: boolean;
  // Location-level sort (only meaningful with include_locations=true).
  // Independent of sort_by/sort_desc so the caller can sort accounts and
  // their nested locations on different axes simultaneously.
  location_sort_by?: string;
  location_sort_desc?: boolean;
}): Promise<any[]> {
  // Include both sort axes in the dedup key so consecutive calls with
  // different sort values don't collapse to a single in-flight request.
  const sortKey = `${opts?.sort_by || ''}:${opts?.sort_desc === undefined ? '' : opts.sort_desc}`;
  // The deleted-account flags change the RESULT SET, so they belong in the
  // dedup key — without them, toggling "Show Disabled" while a request is in
  // flight collapses onto the previous one and the toggle appears to do nothing.
  const deletedKey = `${opts?.include_deleted ? 'inc' : ''}${opts?.exclude_deleted ? 'exc' : ''}`;
  const locSortKey = `${opts?.location_sort_by || ''}:${opts?.location_sort_desc === undefined ? '' : opts.location_sort_desc}`;
  const dedupKey = opts?.account_id
    ? `getAccountsDashboard:${opts.account_id}:${opts.include_locations}:${sortKey}:${locSortKey}:${deletedKey}`
    : opts?.include_locations
      ? `getAccountsDashboard:withLocations:${sortKey}:${locSortKey}:${deletedKey}`
      : `getAccountsDashboard:${sortKey}:${deletedKey}`;
  return dedup(dedupKey, async () => {
    const params: string[] = [];
    if (opts?.include_locations) params.push('include_locations=true');
    if (opts?.include_deleted) params.push('include_deleted=true');
    if (opts?.exclude_deleted) params.push('exclude_deleted=true');
    if (opts?.account_id) params.push(`account_id=${opts.account_id}`);
    if (opts?.sort_by) params.push(`sort_by=${encodeURIComponent(opts.sort_by)}`);
    if (opts?.sort_desc !== undefined) params.push(`sort_desc=${opts.sort_desc ? 'true' : 'false'}`);
    if (opts?.location_sort_by) params.push(`location_sort_by=${encodeURIComponent(opts.location_sort_by)}`);
    if (opts?.location_sort_desc !== undefined) params.push(`location_sort_desc=${opts.location_sort_desc ? 'true' : 'false'}`);
    const query = params.length > 0 ? `?${params.join('&')}` : '';
    const res = await webApiClient.get<any>(`/accounts/dashboard${query}`);
    if (!res.success) {
      if (res.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      } else if (res.status === 403) {
        throw new Error(serverErrorFromBody(res.errorBody) ?? "You don't have permission to view the accounts dashboard.");
      }
      throw new Error(`Failed to fetch accounts dashboard: ${res.status} - ${res.error}`);
    }
    const items = extractItems(res.data, ['accounts']);
    // Normalize dashboard fields (account_id → id, account_name → name, *_count → short names)
    return items.map((item: any) => ({
      ...item,
      id: item.id ?? item.account_id,
      name: item.name ?? item.account_name,
      active: item.active_count ?? item.active ?? 0,
      inspection: item.inspection_count ?? item.inspection ?? 0,
      approval: item.approval_count ?? item.approval ?? 0,
      in_production: item.in_production_count ?? item.in_production ?? 0,
      closed: item.closed_count ?? item.closed ?? 0,
      daily_revenue: item.daily_revenue ?? 0,
      mtd_revenue: item.mtd_revenue ?? 0,
    }));
  });
}

export async function getAccount(accountId: string): Promise<any> {
  return dedup(`getAccount:${accountId}`, async () => {
    const res = await webApiClient.get<any>(`/accounts/${accountId}`);
    if (!res.success) {
      throw new Error(`Failed to fetch account: ${res.status} - ${res.error}`);
    }
    return res.data;
  });
}

export async function createAccount(account: any): Promise<any> {
  const res = await webApiClient.post<any>('/accounts', account);
  if (!res.success) {
    const error = new Error(res.errorBody?.error || 'Failed to create account');
    console.error('Create account error:', error);
    throw error;
  }
  refreshAccountNames();
  return res.data;
}

export async function updateAccount(accountId: string, account: any): Promise<any> {
  // If the account has a 'name' field, we need to use the settings endpoint
  const hasNameField = account.name !== undefined;
  let result: any;

  if (hasNameField) {
    // Split the payload - name goes to settings, rest goes to account
    const { name, ...accountData } = account;
    const settingsData = { name };

    // Update settings first (name)
    const settingsRes = await webApiClient.put<any>(`/accounts/${accountId}/settings`, settingsData);
    if (!settingsRes.success) {
      throw new Error(settingsRes.errorBody?.error || 'Failed to update account settings');
    }
    result = settingsRes.data;

    // If there are other fields to update, update them via the main account endpoint
    if (Object.keys(accountData).length > 0) {
      const accountRes = await webApiClient.put<any>(`/accounts/${accountId}`, accountData);
      if (!accountRes.success) {
        throw new Error(accountRes.errorBody?.error || 'Failed to update account data');
      }
      // Get the final result after both updates
      result = accountRes.data;
    }
  } else {
    // No name field, use regular account endpoint
    const res = await webApiClient.put<any>(`/accounts/${accountId}`, account);
    if (!res.success) {
      throw new Error(res.errorBody?.error || 'Failed to update account');
    }
    result = res.data;
  }

  refreshAccountNames();
  return result;
}

export async function deleteAccount(accountId: string): Promise<void> {
  const res = await webApiClient.delete<any>(`/accounts/${accountId}`);
  if (!res.success) {
    const error = new Error(res.errorBody?.error || 'Failed to delete account');
    console.error('Delete account error:', error);
    throw error;
  }
  refreshAccountNames();
}

export async function undeleteAccount(accountId: string): Promise<void> {
  const res = await webApiClient.post<any>(`/accounts/${accountId}/undelete`);
  if (!res.success) {
    const error = new Error(res.errorBody?.error || 'Failed to undelete account');
    console.error('Undelete account error:', error);
    throw error;
  }
  refreshAccountNames();
}

// ---------------------------------------------------------------------------
// Session context (account/location switching, current-user context)
// ---------------------------------------------------------------------------

// Get current user's account and location information
export async function getCurrentUserContext(): Promise<any> {
  const res = await webApiClient.get<any>('/auth/me');
  if (!res.success) {
    const error = new Error(`Failed to fetch user context: ${res.status} - ${res.error}`);
    console.error('Get user context error:', error);
    throw error;
  }
  return res.data;
}

// Get list of accounts the current user is a member of
export async function getMyAccounts(): Promise<{ id: string; name: string; active_job_count?: number }[]> {
  const res = await webApiClient.get<any>('/auth/accounts');
  if (!res.success) {
    // Original implementation swallowed every failure and returned [].
    console.error('Get user accounts error:', new Error(`Failed to fetch user accounts: ${res.status} - ${res.error}`));
    return [];
  }
  const data = res.data;
  // Response format: { accounts: [{ id, name, active_job_count, ... }] }
  return (data.accounts || []).map((a: any) => ({
    id: a.id,
    name: a.name,
    active_job_count: a.active_job_count || 0,
  }));
}

// Switch the active account context (re-scopes the JWT cookie)
export async function switchAccount(
  accountId: string,
  locationId?: string
): Promise<{ message: string; session: any; permissions: string[] }> {
  const res = await webApiClient.post<{ message: string; session: any; permissions: string[] }>('/auth/switch-account', {
    account_id: accountId,
    ...(locationId ? { location_id: locationId } : {}),
  });
  if (!res.success) {
    throw new Error(`Failed to switch account: ${res.status} - ${res.error}`);
  }
  return res.data as { message: string; session: any; permissions: string[] };
}

// Move the session to a work context (re-scopes the JWT cookie). A location
// moves the session into its account; an account alone lands on the last
// location used there. The server answers with the context it resolved —
// callers adopt THAT, never their own guess. See turn-connect auth_context.go.
export async function switchContext(target: WorkContextTarget): Promise<SwitchContextResult> {
  const res = await webApiClient.post<any>('/auth/switch-context', {
    ...(target.accountId ? { account_id: target.accountId } : {}),
    ...(target.locationId ? { location_id: target.locationId } : {}),
    ...(target.allLocations ? { all_locations: true } : {}),
  });
  if (!res.success || !res.data?.context) {
    const message = serverErrorFromBody(res.errorBody) || res.error || 'Failed to switch';
    throw new Error(message);
  }
  return {
    context: res.data.context,
    permissions: Array.isArray(res.data.permissions) ? res.data.permissions : [],
    account_permissions: Array.isArray(res.data.account_permissions) ? res.data.account_permissions : [],
  };
}

// Every account the person can work in, with its locations and active job
// counts (GET /auth/contexts) — the list behind the Departments location
// switcher, the same one the mobile header uses.
export async function listContexts(): Promise<ContextsList> {
  const res = await webApiClient.get<any>('/auth/contexts');
  if (!res.success || !res.data) {
    throw new Error(serverErrorFromBody(res.errorBody) || res.error || 'Failed to load your accounts and locations');
  }
  return { current: res.data.current, accounts: Array.isArray(res.data.accounts) ? res.data.accounts : [] };
}

// Get list of locations the current user has access to, with active job counts
export async function getMyLocations(): Promise<{ id: string; name: string; account_id: string; active_job_count: number }[]> {
  const res = await webApiClient.get<any>('/auth/my-locations');
  if (!res.success) {
    // Original implementation swallowed every failure and returned [].
    console.error('Get user locations error:', new Error(`Failed to fetch user locations: ${res.status} - ${res.error}`));
    return [];
  }
  const data = res.data;
  return (data.locations || []).map((l: any) => ({
    id: l.id,
    name: l.name,
    account_id: l.account_id,
    active_job_count: l.active_job_count || 0,
  }));
}

/**
 * The locations this person can open in one account (GET /auth/my-locations
 * ?account_id=): every live location for an account administrator, the
 * assigned ones (and department grants) for everyone else. An empty list on
 * failure, so a picker simply hides.
 */
export async function getMyLocationsInAccount(accountId: string): Promise<{ id: string; name: string; account_id: string }[]> {
  const res = await webApiClient.get<any>(`/auth/my-locations?account_id=${encodeURIComponent(accountId)}`);
  if (!res.success) return [];
  return (res.data?.locations || []).map((l: any) => ({ id: l.id, name: l.name, account_id: l.account_id }));
}

/**
 * Searches the locations this person can open in one account, a page at a
 * time (GET /auth/my-locations?account_id=&q=&limit=&offset=). For pickers on
 * accounts with hundreds of locations. Throws on failure.
 */
export async function searchMyLocationsInAccount(
  accountId: string,
  query: string,
  limit = 20,
  offset = 0
): Promise<{ locations: { id: string; name: string }[]; total: number }> {
  const params = new URLSearchParams({ account_id: accountId, limit: String(limit), offset: String(offset) });
  if (query.trim()) params.set('q', query.trim());
  const res = await webApiClient.get<any>(`/auth/my-locations?${params.toString()}`);
  if (!res.success) throw new Error(res.error || `Failed to search locations (${res.status})`);
  const locations = (res.data?.locations || []).map((l: any) => ({ id: l.id, name: l.name }));
  return { locations, total: typeof res.data?.total === 'number' ? res.data.total : locations.length };
}

/**
 * This person's permissions AT a location (GET /auth/me/permissions
 * ?location_id=): the same set the API checks changes there against, which
 * can differ from the session's location. Throws on failure (404 when the
 * location is not one they can open), so callers fall back to the session set.
 */
export async function getMyPermissionsAt(locationId: string): Promise<string[]> {
  const res = await webApiClient.get<any>(`/auth/me/permissions?location_id=${encodeURIComponent(locationId)}`);
  if (!res.success) throw new Error(res.error || `Failed to load permissions (${res.status})`);
  return Array.isArray(res.data?.permissions) ? res.data.permissions : [];
}

// Switch active location for current user session
// Returns the updated permissions array from the new location-scoped JWT
export async function switchLocation(locationId: string): Promise<string[]> {
  const res = await webApiClient.post<any>('/auth/switch-location', { location_id: locationId });
  if (!res.success) {
    // Prefer the structured backend message (`error` then `message`), then
    // the client's extracted error text, then the generic fallback —
    // mirrors the original raw-body parse chain.
    const message = serverErrorFromBody(res.errorBody) || res.error || 'Failed to switch location';
    throw new Error(message);
  }
  const data = res.data;
  return Array.isArray(data?.permissions) ? data.permissions : [];
}

/**
 * Returns the locations the current user has any role on, each enriched
 * with the user's roles at that location and the flattened set of
 * permission keys those roles grant. Use this — not getLocations or the
 * older getMyLocations() helper — whenever the UI needs to filter a
 * dropdown by what the current user is actually authorized to do (e.g.,
 * the Reports page filters out locations where the user lacks
 * reports:read:*). Backed by GET /api/users/me/locations.
 *
 * Distinct from getMyLocations() which hits /auth/my-locations and is
 * shaped for the location switcher (id, name, account_id, active_job_count).
 */
export async function getMyLocationsWithRoles(): Promise<
  Array<{
    location: Location;
    roles: Array<{ id: string; name: string }>;
    permissions: string[];
  }>
> {
  return dedup('getMyLocationsWithRoles', async () => {
    const res = await webApiClient.get<any>('/users/me/locations');
    if (!res.success) {
      throw new Error(`Failed to fetch my locations: ${res.status} - ${res.error}`);
    }
    // Endpoint returns a bare array; extractItems handles both shapes safely.
    return extractItems(res.data) as Array<{
      location: Location;
      roles: Array<{ id: string; name: string }>;
      permissions: string[];
    }>;
  });
}

// ---------------------------------------------------------------------------
// Activities / audit feed
// ---------------------------------------------------------------------------

export async function getAccountActivities(
  accountId: string,
  limit: number = 20,
  fromDate?: string
): Promise<{ activities: any[]; total: number }> {
  let url = `/accounts/${accountId}/activities?limit=${limit}`;
  if (fromDate) {
    url += `&from_date=${encodeURIComponent(fromDate)}`;
  }
  const res = await webApiClient.get<any>(url);
  if (!res.success) {
    throw new Error(`Failed to fetch account activities: ${res.status} - ${res.error}`);
  }
  const json = res.data;
  return {
    activities: extractItems(json, ['activities', 'data']),
    total: json.total || json.pagination?.total || 0,
  };
}

export async function getLocationActivities(
  locationId: string,
  limit: number = 20,
  fromDate?: string
): Promise<{ activities: any[]; total: number }> {
  let url = `/locations/${locationId}/activities?limit=${limit}`;
  if (fromDate) {
    url += `&from_date=${encodeURIComponent(fromDate)}`;
  }
  const res = await webApiClient.get<any>(url);
  if (!res.success) {
    throw new Error(`Failed to fetch location activities: ${res.status} - ${res.error}`);
  }
  const json = res.data;
  return {
    activities: extractItems(json, ['activities', 'data']),
    total: json.total || json.pagination?.total || 0,
  };
}

export async function getAllActivities(limit: number = 25, fromDate?: string): Promise<{ activities: any[]; total: number }> {
  let url = `/activities?limit=${limit}`;
  if (fromDate) {
    url += `&from_date=${encodeURIComponent(fromDate)}`;
  }
  const res = await webApiClient.get<any>(url);
  if (!res.success) {
    throw new Error(`Failed to fetch all activities: ${res.status} - ${res.error}`);
  }
  const json = res.data;
  return {
    activities: extractItems(json, ['activities', 'data']),
    total: json.total || json.pagination?.total || 0,
  };
}

export async function getActivitiesByResourceId(
  locationId: string,
  resourceId: string,
  limit: number = 50
): Promise<{ activities: any[]; total: number }> {
  const url = `/locations/${locationId}/activities?resource_id=${resourceId}&limit=${limit}`;
  const res = await webApiClient.get<any>(url);
  if (!res.success) {
    throw new Error(`Failed to fetch activities for resource: ${res.status} - ${res.error}`);
  }
  const json = res.data;
  return {
    activities: extractItems(json, ['items', 'activities', 'data']),
    total: json.total || json.pagination?.total || 0,
  };
}

/**
 * List activities scoped to a parent resource (e.g. all delivery audit rows
 * that live under an invoice batch). Backend: GET /api/accounts/{id}/activities?parent_resource_id=...
 */
export async function listActivitiesByBatch(accountId: string, batchId: string, limit: number = 50): Promise<Activity[]> {
  const params = new URLSearchParams({ parent_resource_id: batchId, limit: String(limit) });
  const res = await webApiClient.get<any>(`/accounts/${accountId}/activities?${params.toString()}`);
  if (!res.success) {
    throw new Error(`Failed to fetch batch activities: ${res.status} - ${res.error}`);
  }
  return extractItems(res.data, ['items', 'activities', 'data']) as Activity[];
}

// Fetches the lazy-loaded detail payload for a single audit row. Returns
// the raw activity row, a resolved_refs array mapping every UUID-shaped
// metadata field to a display name + canonical resource type, and (for
// bulk-import rows) an inline first page of activity_import_details.
// Powers the User Audit modal — GET /api/activities/:id/detail.
export async function getActivityDetail(
  activityId: string,
  importDetailLimit: number = 50
): Promise<{
  activity: any;
  resolved_refs: Array<{ key: string; type: string; id: string; display_name: string }>;
  import_details?: Array<{
    id: string;
    activity_id: string;
    row_number: number;
    action: string;
    summary?: string;
    before_values?: any;
    after_values?: any;
    error_message?: string;
    created_at: string;
  }>;
  import_details_total?: number;
}> {
  const res = await webApiClient.get<any>(`/activities/${activityId}/detail?import_detail_limit=${importDetailLimit}`);
  if (!res.success) {
    throw new Error(`Failed to fetch activity detail: ${res.status} - ${res.error}`);
  }
  return res.data;
}

// ---------------------------------------------------------------------------
// Composed export
// ---------------------------------------------------------------------------

export const accountsContextAdmin = {
  // Account logo
  getAccountLogoUrl,
  uploadAccountLogo,
  // Accounts CRUD + dashboard
  getAllAccounts,
  getAccountSummaries,
  listAccountsPage,
  getAccountsDashboard,
  getAccount,
  createAccount,
  updateAccount,
  deleteAccount,
  undeleteAccount,
  // Session context
  getCurrentUserContext,
  getMyAccounts,
  switchAccount,
  switchContext,
  listContexts,
  getMyLocations,
  getMyLocationsInAccount,
  searchMyLocationsInAccount,
  getMyPermissionsAt,
  switchLocation,
  getMyLocationsWithRoles,
  // Activities / audit feed
  getAccountActivities,
  getLocationActivities,
  getAllActivities,
  getActivitiesByResourceId,
  listActivitiesByBatch,
  getActivityDetail,
};
